import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { LucideAngularModule, Search, X, Calendar, MapPin, Ban, CheckCircle } from 'lucide-angular';
import { debounceTime, map, startWith, takeUntil } from 'rxjs';
import { Destroyer } from '@app/shared/abstract/destroyer';
import { EventService } from '@app/events/shared/event.service';
import { EventModel } from '@app/events/shared/event.model';
import { RouteParams } from '@app/shared/abstract/route-params';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { DatePipe, NgClass } from '@angular/common';
import { NotificationService } from '@app/shared/notification/notification.service';

@Component({
  selector: 'app-event-selection-form',
  template: `
    <div class="p-6">
      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <div>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Select Event</h2>
          <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Choose an event to apply the "{{ templateName }}" template
          </p>
        </div>
        <button
          type="button"
          (click)="formCancelled.emit()"
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
          <lucide-icon [name]="XIcon" [size]="20"></lucide-icon>
        </button>
      </div>

      <!-- Search -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Search Events
        </label>
        <div class="relative">
          <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <lucide-icon [name]="SearchIcon" [size]="16" class="text-gray-400"></lucide-icon>
          </div>
          <input
            [formControl]="searchControl"
            type="text"
            placeholder="Search events..."
            class="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent">
        </div>
      </div>

      <!-- Event list -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Available Events
        </label>
        
        <div class="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg">
          @if (loading) {
            <div class="flex items-center justify-center py-8">
              <div class="text-gray-500 dark:text-gray-400">Loading events...</div>
            </div>
          } @else if (filteredEvents.length === 0) {
            <div class="flex items-center justify-center py-8">
              <div class="text-center">
                <lucide-icon [name]="CalendarIcon" [size]="48" class="mx-auto mb-4 text-gray-300 dark:text-gray-600"></lucide-icon>
                <div class="text-gray-500 dark:text-gray-400">No events found</div>
                <div class="text-sm text-gray-400 dark:text-gray-500 mt-1">Try adjusting your search</div>
              </div>
            </div>
          } @else {
            @for (event of filteredEvents; track event.id) {
              <div
                (click)="selectEvent(event)"
                [ngClass]="{
                  'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700': selectedEvent?.id === event.id,
                  'hover:bg-gray-50 dark:hover:bg-gray-700': selectedEvent?.id !== event.id,
                  'opacity-50 cursor-not-allowed': hasTemplate(event),
                  'cursor-pointer': !hasTemplate(event)
                }"
                class="border-b border-gray-200 dark:border-gray-600 p-4 transition-colors">
                <div class="flex items-center justify-between">
                  <div class="flex-1">
                    <div class="flex items-center gap-3">
                      <h4 class="font-medium text-gray-900 dark:text-white">{{ event.name }}</h4>
                      <span 
                        class="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full"
                        [ngClass]="{
                          'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200': event.status === 'Confirmed',
                          'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200': event.status === 'Provisional',
                          'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200': event.status === 'Opportunity',
                          'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200': event.status === 'Cancelled',
                          'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200': event.status === 'Closed'
                        }">
                        {{ event.status }}
                      </span>
                      @if (hasTemplate(event)) {
                        <span class="inline-flex items-center px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full dark:bg-red-900 dark:text-red-200">
                          Template Applied
                        </span>
                      }
                    </div>
                    <div class="mt-1 flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                      <div class="flex items-center gap-1">
                        <lucide-icon [name]="CalendarIcon" [size]="14"></lucide-icon>
                        {{ event.start_date | date:'MMM d, yyyy' }}
                      </div>
                      <div class="flex items-center gap-1">
                        <lucide-icon [name]="MapPinIcon" [size]="14"></lucide-icon>
                        {{ event.venue?.name || 'No venue' }}
                      </div>
                    </div>
                    @if (hasTemplate(event)) {
                      <div class="mt-2 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                        <lucide-icon [name]="BanIcon" [size]="14" class="shrink-0"></lucide-icon>
                        <span>This event already has a template applied. Cannot apply multiple templates.</span>
                      </div>
                    }
                  </div>
                  @if (selectedEvent?.id === event.id && !hasTemplate(event)) {
                    <div class="ml-4 text-blue-600 dark:text-blue-400 flex items-center gap-1">
                      <lucide-icon [name]="CheckCircleIcon" [size]="16" class="shrink-0"></lucide-icon>
                      <span>Selected</span>
                    </div>
                  }
                </div>
              </div>
            }
          }
        </div>
      </div>

      <!-- Actions -->
      <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-600">
        <app-button
          [label]="'Cancel'"
          [style]="'secondary'"
          (click)="formCancelled.emit()">
        </app-button>
        <app-button
          [label]="'Apply Template'"
          [style]="'primary'"
          [disabled]="!selectedEvent || (selectedEvent && hasTemplate(selectedEvent))"
          (click)="applyTemplate()">
        </app-button>
      </div>
    </div>
  `,
  imports: [
    LucideAngularModule,
    ReactiveFormsModule,
    ButtonComponent,
    DatePipe,
    NgClass
  ],
  standalone: true
})
export class EventSelectionFormComponent extends Destroyer implements OnInit {
  @Input() templateName = '';
  @Output() eventSelected = new EventEmitter<EventModel>();
  @Output() formCancelled = new EventEmitter<void>();

  protected readonly SearchIcon = Search;
  protected readonly XIcon = X;
  protected readonly CalendarIcon = Calendar;
  protected readonly MapPinIcon = MapPin;
  protected readonly BanIcon = Ban;
  protected readonly CheckCircleIcon = CheckCircle;

  searchControl = new FormControl('');
  selectedEvent: EventModel | null = null;
  events: EventModel[] = [];
  filteredEvents: EventModel[] = [];
  loading = false;

  constructor(
    private eventService: EventService,
    private notificationService: NotificationService
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadEvents();
    
    // Setup search filtering
    this.searchControl.valueChanges
      .pipe(
        startWith(''),
        debounceTime(300),
        takeUntil(this.destroy$)
      )
      .subscribe((searchTerm) => {
        this.filterEvents(searchTerm || '');
      });
  }

  private loadEvents(): void {
    this.loading = true;
    
    const routeParams = new RouteParams({
      pageNum: 1,
      pageSize: 100,
      sort: [{ field: 'start_date', dir: 'desc' }]
    });

    this.eventService.getPage(routeParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.events = response.data;
          this.filterEvents(this.searchControl.value || '');
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading events:', error);
          this.notificationService.error('Failed to load events');
          this.loading = false;
        }
      });
  }

  private filterEvents(searchTerm: string): void {
    if (!searchTerm.trim()) {
      this.filteredEvents = this.events;
      return;
    }

    const term = searchTerm.toLowerCase();
    this.filteredEvents = this.events.filter(event =>
      event.name.toLowerCase().includes(term) ||
      event.venue?.name?.toLowerCase().includes(term) ||
      event.status.toLowerCase().includes(term)
    );
  }

  selectEvent(event: EventModel): void {
    if (this.hasTemplate(event)) {
      return; // Cannot select events that already have templates
    }
    this.selectedEvent = event;
  }

  applyTemplate(): void {
    if (this.selectedEvent && !this.hasTemplate(this.selectedEvent)) {
      this.eventSelected.emit(this.selectedEvent);
    }
  }

  hasTemplate(event: EventModel): boolean {
    return event.template_id !== null && event.template_id !== undefined;
  }
}
